import { DefinitionItem, DefinitionList, EmptyState, LoadingState, PhoneNumber, View } from "components/Shared";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";

/**
 * The people on this lease — `Contacts[]` from the /lease_data payload
 * (GET /v2/contacts?lease_agreement_id=:id).
 *
 * A Contact BELONGS TO THE LEASE AGREEMENT, not to a project: the payload
 * carries Contacts[] as a property of the lease, and
 * `Amendment::PayloadWriter#sync_contacts!` full-replaces that agreement's
 * people on every amendment mapped to write "contact". So this is the current
 * list for the whole lease — the project workspace's Contacts tab shows exactly
 * the same rows, one hop further out through `project.lease_agreement`.
 */

interface Props {
	leaseAgreementId: string;
	name?: string;
	icon?: string;
}

const ContactsTab = ({ leaseAgreementId }: Props) => {
	// null = no response yet. Driven from our own state rather than
	// `datasource.isLoading`: with no `renderTrigger` the datasource cannot
	// re-render this component when it flips isLoading back to false, which would
	// leave the tab stuck on <LoadingState />.
	const [contacts, setContacts] = useState<any[] | null>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "contact" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setContacts(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(leaseAgreementId))
			datasource.get("v2", "contacts", {
				queryStringParameters: { lease_agreement_id: leaseAgreementId, per_page: 1000 }
			});
	}, [datasource, leaseAgreementId]);

	if (contacts === null) return <LoadingState />;

	// The shared empty state, so a lease with no contacts reads like every other
	// empty list in the app rather than like a stray line of text (Sim, 24 Aug).
	if (_.isEmpty(contacts))
		return (
			<View>
				<EmptyState resource="contacts" />
			</View>
		);

	return (
		<View>
			{_.map(contacts, (contact, index) => (
				<React.Fragment key={contact.id}>
					{index > 0 && <hr style={{ margin: "1.5rem 0" }} />}
					<View title={_.get(contact, "name")}>
						<DefinitionList>
							<DefinitionItem title="role" value={_.get(contact, "role")} />
							<DefinitionItem title="contact_name" value={_.get(contact, "name")} />
							<DefinitionItem title="contact_email" value={_.get(contact, "email")} />
							<DefinitionItem
								title="phone_number"
								value={<PhoneNumber phone_number={_.get(contact, "mobile_number")} />}
							/>
						</DefinitionList>
					</View>
				</React.Fragment>
			))}
		</View>
	);
};

export default memo(ContactsTab);
